import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, setDoc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { UserProfile } from '@/types';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Search, Crown, User as UserIcon, Shield, ShieldOff, Edit, X } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

export const AdminUsers = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [adminUsers, setAdminUsers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState<'all' | 'Free' | 'Standard'>('all');
  
  // プラン編集モーダル
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [planForm, setPlanForm] = useState({
    plan: 'Free' as 'Free' | 'Standard',
    planEndDate: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchAdminUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const usersData = snapshot.docs.map((doc) => ({
        uid: doc.id,
        ...doc.data(),
      })) as UserProfile[];
      
      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const adminsRef = collection(db, 'admins');
      const snapshot = await getDocs(adminsRef);
      const adminIds = new Set(snapshot.docs.map((doc) => doc.id));
      setAdminUsers(adminIds);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
    }
  };

  const toggleAdminRole = async (userId: string, isCurrentlyAdmin: boolean) => {
    const confirmMessage = isCurrentlyAdmin
      ? '管理者権限を削除しますか？'
      : '管理者権限を付与しますか？';
    
    if (!confirm(confirmMessage)) {
      return;
    }
    
    try {
      const adminDocRef = doc(db, 'admins', userId);
      
      if (isCurrentlyAdmin) {
        // 管理者権限を削除
        await deleteDoc(adminDocRef);
        setAdminUsers((prev) => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
        alert('管理者権限を削除しました。');
      } else {
        // 管理者権限を付与
        await setDoc(adminDocRef, {
          uid: userId,
          grantedAt: new Date(),
        });
        setAdminUsers((prev) => new Set([...prev, userId]));
        alert('管理者権限を付与しました。ユーザーは次回ログイン時から管理画面にアクセスできます。');
      }
    } catch (error) {
      console.error('Failed to toggle admin role:', error);
      alert('管理者権限の変更に失敗しました。');
    }
  };

  const handleOpenEditModal = (user: UserProfile) => {
    setEditingUser(user);
    setPlanForm({
      plan: user.plan,
      planEndDate: user.planEndDate ? format(user.planEndDate.toDate(), 'yyyy-MM-dd') : '',
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const userRef = doc(db, 'users', editingUser.uid);
      const updateData: Partial<UserProfile> = {
        plan: planForm.plan,
        planStartDate: Timestamp.now(),
        planEndDate: planForm.planEndDate ? Timestamp.fromDate(new Date(planForm.planEndDate)) : null,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(userRef, updateData);
      
      // ローカルステートを更新
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === editingUser.uid
            ? { ...u, ...updateData }
            : u
        )
      );

      alert('プラン情報を更新しました。');
      handleCloseEditModal();
      fetchUsers(); // 再取得して最新状態に
    } catch (error) {
      console.error('Failed to update user plan:', error);
      alert('プラン情報の更新に失敗しました。');
    }
  };

  useEffect(() => {
    let result = users;

    // プランフィルター
    if (filterPlan !== 'all') {
      result = result.filter((user) => user.plan === filterPlan);
    }

    // 検索フィルター
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (user) =>
          user.displayName.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
      );
    }

    setFilteredUsers(result);
  }, [users, searchQuery, filterPlan]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ページヘッダー */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ユーザー管理</h1>
          <p className="text-gray-600 mt-1">全{users.length}件のユーザー</p>
        </div>

        {/* フィルターバー */}
        <div className="card">
          <div className="flex flex-col md:flex-row gap-4">
            {/* 検索 */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="名前またはメールアドレスで検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>

            {/* プランフィルター */}
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value as 'all' | 'Free' | 'Standard')}
              className="input w-full md:w-48"
            >
              <option value="all">すべてのプラン</option>
              <option value="Free">無料プラン</option>
              <option value="Standard">Standardプラン</option>
            </select>
          </div>
        </div>

        {/* ユーザーテーブル */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ユーザー
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    プラン
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    登録日
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    招待コード
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    管理者権限
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const isAdmin = adminUsers.has(user.uid);
                  
                  return (
                    <tr key={user.uid} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <img
                            src={user.photoURL || '/images/sidepocket_symbol.png'}
                            alt={user.displayName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              {user.displayName}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.plan === 'Standard'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {user.plan === 'Standard' && <Crown className="w-3 h-3" />}
                            {user.plan === 'Free' && <UserIcon className="w-3 h-3" />}
                            {user.plan}
                          </span>
                          {user.planEndDate && (
                            <span className="text-xs text-gray-500">
                              {format(user.planEndDate.toDate(), 'yyyy/MM/dd', { locale: ja })}まで
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt && format(user.createdAt.toDate(), 'yyyy/MM/dd')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {user.invitationCode}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {isAdmin && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <Shield className="w-3 h-3" />
                            管理者
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEditModal(user)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition"
                          >
                            <Edit className="w-3 h-3" />
                            プラン編集
                          </button>
                          <button
                            onClick={() => toggleAdminRole(user.uid, isAdmin)}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
                              isAdmin
                                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                            }`}
                          >
                            {isAdmin ? (
                              <>
                                <ShieldOff className="w-3 h-3" />
                                権限削除
                              </>
                            ) : (
                              <>
                                <Shield className="w-3 h-3" />
                                権限付与
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 結果なし */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">該当するユーザーが見つかりませんでした。</p>
          </div>
        )}

        {/* プラン編集モーダル */}
        {isEditModalOpen && editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">プラン編集</h3>
                  <button
                    onClick={handleCloseEditModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">ユーザー</p>
                  <p className="font-medium text-gray-900">{editingUser.displayName}</p>
                  <p className="text-sm text-gray-500">{editingUser.email}</p>
                </div>

                <form onSubmit={handleUpdatePlan} className="space-y-4">
                  <div>
                    <label htmlFor="plan" className="block text-sm font-medium text-gray-700 mb-1">
                      プラン <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="plan"
                      value={planForm.plan}
                      onChange={(e) => setPlanForm({ ...planForm, plan: e.target.value as 'Free' | 'Standard' })}
                      className="input w-full"
                      required
                    >
                      <option value="Free">Free</option>
                      <option value="Standard">Standard</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="planEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                      プラン終了日
                    </label>
                    <input
                      type="date"
                      id="planEndDate"
                      value={planForm.planEndDate}
                      onChange={(e) => setPlanForm({ ...planForm, planEndDate: e.target.value })}
                      className="input w-full"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      未指定の場合は無期限になります
                    </p>
                  </div>

                  <div className="flex items-center gap-3 pt-4">
                    <button
                      type="submit"
                      className="btn-primary flex-1"
                    >
                      更新する
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseEditModal}
                      className="btn-secondary flex-1"
                    >
                      キャンセル
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
